import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  platformMaintenance,
  type MaintenanceWindow,
  type MaintenanceWindowStatus,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

const situacoes: Record<
  MaintenanceWindowStatus,
  { rotulo: string; tom: 'warning' | 'info' | 'neutral' }
> = {
  em_andamento: { rotulo: 'Em andamento', tom: 'warning' },
  agendada: { rotulo: 'Agendada', tom: 'info' },
  encerrada: { rotulo: 'Encerrada', tom: 'neutral' },
  cancelada: { rotulo: 'Cancelada', tom: 'neutral' },
};

/** Converte o valor do campo de data e hora local em instante ISO. */
function instante(valor: string): string | undefined {
  if (!valor) return undefined;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? undefined : data.toISOString();
}

/**
 * Janela de manutencao da plataforma (o /maintenance do OnPoint).
 *
 * A janela em andamento leva a tela de todas as pessoas de todas as empresas para a
 * manutencao; quem administra a plataforma continua entrando, para encerra-la. A
 * janela agendada vira aviso na moldura de todas as empresas. Cada abertura,
 * encerramento e cancelamento vai para a trilha da plataforma.
 *
 * O aviso de manutencao de uma empresa so e outra coisa: e escrito por quem
 * administra a empresa, na aba Acesso das configuracoes dela.
 */
export function PlatformMaintenancePage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [mensagem, setMensagem] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');

  const consulta = useQuery({
    queryKey: ['platform', 'manutencao'],
    queryFn: ({ signal }) => platformMaintenance.list(signal),
  });

  const atualizar = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'manutencao'] });
    void queryClient.invalidateQueries({ queryKey: ['acesso', 'manutencao'] });
  };

  const abrir = useMutation({
    mutationFn: () => {
      const comeco = instante(inicio);
      const termino = instante(fim);
      return platformMaintenance.create({
        message: mensagem,
        ...(comeco ? { starts_at: comeco } : {}),
        ...(termino ? { ends_at: termino } : {}),
      });
    },
    onSuccess: (janela) => {
      toast.success(
        janela.status === 'em_andamento'
          ? 'Manutencao em andamento. As empresas estao vendo a tela de manutencao.'
          : 'Janela de manutencao agendada.',
      );
      setMensagem('');
      setInicio('');
      setFim('');
      atualizar();
    },
  });

  const mudar = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'encerrar' | 'cancelar' }) =>
      platformMaintenance.update(id, { action }),
    onSuccess: (janela) => {
      toast.success(
        janela.status === 'cancelada' ? 'Janela cancelada.' : 'Manutencao encerrada.',
      );
      atualizar();
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Nao foi possivel alterar a janela.',
      );
    },
  });

  const erroDeAbertura = abrir.error instanceof ApiError ? abrir.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    abrir.mutate();
  }

  const colunas: Column<MaintenanceWindow>[] = [
    {
      key: 'situacao',
      header: 'Situacao',
      render: (janela) => (
        <Badge tone={situacoes[janela.status].tom}>
          {situacoes[janela.status].rotulo}
        </Badge>
      ),
    },
    {
      key: 'inicio',
      header: 'Inicio',
      render: (janela) => (
        <span className="tabular-nums">{formatDateTime(janela.starts_at)}</span>
      ),
    },
    {
      key: 'fim',
      header: 'Fim previsto',
      render: (janela) =>
        janela.ends_at ? (
          <span className="tabular-nums">{formatDateTime(janela.ends_at)}</span>
        ) : (
          <span className="text-ink-500">Sem previsao</span>
        ),
    },
    {
      key: 'mensagem',
      header: 'Mensagem',
      render: (janela) => (
        <span className="line-clamp-2 text-ink-700">{janela.message}</span>
      ),
    },
    {
      key: 'autor',
      header: 'Aberta por',
      hideBelow: 'md',
      render: (janela) => <span className="text-ink-600">{janela.created_by}</span>,
    },
    {
      key: 'acoes',
      header: '',
      align: 'right',
      render: (janela) =>
        janela.status === 'em_andamento' ? (
          // Encerrar a janela em andamento desliga a manutencao: o botao de ligar, em
          // amarelo. Cancelar a agendada a desfaz antes de comecar.
          <AcaoComIcone
            acao="desativar"
            rotulo="Encerrar agora"
            loading={mudar.isPending}
            onClick={() => mudar.mutate({ id: janela.id, action: 'encerrar' })}
          />
        ) : janela.status === 'agendada' ? (
          <AcaoComIcone
            acao="cancelar"
            rotulo="Cancelar"
            loading={mudar.isPending}
            onClick={() => mudar.mutate({ id: janela.id, action: 'cancelar' })}
          />
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Manutencao da plataforma"
        description="Janelas em que a plataforma fica fora do ar para todas as empresas."
      />

      <Card>
        <CardHeader
          title="Abrir janela de manutencao"
          description="Sem inicio, comeca agora. Sem fim, fica sem previsao de retorno ate ser encerrada."
        />
        <CardBody>
          <form onSubmit={enviar} className="flex flex-col gap-4">
            {abrir.isError && !fieldErrorOf(erroDeAbertura, 'message') ? (
              <Alert tone="danger" title="Nao foi possivel abrir a janela">
                {abrir.error.message}
              </Alert>
            ) : null}
            <Alert tone="warning">
              Uma janela em andamento leva a tela de todas as pessoas de todas as
              empresas para a manutencao. Voce continua entrando para encerra-la.
            </Alert>
            <Textarea
              label="Mensagem da manutencao"
              value={mensagem}
              rows={3}
              maxLength={500}
              error={fieldErrorOf(erroDeAbertura, 'message')}
              onChange={(evento) => setMensagem(evento.target.value)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Inicio da manutencao"
                type="datetime-local"
                value={inicio}
                error={fieldErrorOf(erroDeAbertura, 'starts_at')}
                onChange={(evento) => setInicio(evento.target.value)}
              />
              <Field
                label="Fim previsto"
                type="datetime-local"
                value={fim}
                error={fieldErrorOf(erroDeAbertura, 'ends_at')}
                onChange={(evento) => setFim(evento.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" loading={abrir.isPending}>
                Abrir janela de manutencao
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Janelas" description="As cinquenta mais recentes." />
        {consulta.isError ? (
          <CardBody>
            <Alert tone="danger" title="Nao foi possivel carregar as janelas">
              {consulta.error.message}
            </Alert>
          </CardBody>
        ) : (
          <DataTable
            columns={colunas}
            items={consulta.data ?? []}
            keyOf={(janela) => janela.id}
            loading={consulta.isPending}
            empty="Nenhuma janela de manutencao registrada."
          />
        )}
      </Card>
    </div>
  );
}
